// Net worth: manual accounts and debts, amortising loans fed from the calculator, and periodic snapshots.
import { convert } from "./fx.js";

const DAY = 86400000;
const ms = (d) => Date.parse(d + "T00:00:00Z");
export const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export const ACCOUNT_KINDS = [
  { id: "cash", label: "Cash", cls: "Cash" },
  { id: "bank", label: "Bank account", cls: "Cash" },
  { id: "retirement", label: "Retirement & tax-advantaged", cls: "Retirement" },
  { id: "property", label: "Property", cls: "Property" },
  { id: "vehicle", label: "Vehicle", cls: "Other" },
  { id: "other", label: "Other asset", cls: "Other" },
];
export const LIABILITY_KINDS = [
  { id: "mortgage", label: "Mortgage" },
  { id: "loan", label: "Loan" },
  { id: "card", label: "Credit card" },
  { id: "other", label: "Other debt" },
];

// Retirement and tax-advantaged account types by the country on the profile.
const RET = {
  AU: [["super", "Superannuation"], ["smsf", "Self-managed super (SMSF)"]],
  NZ: [["kiwisaver", "KiwiSaver"]],
  CA: [["rrsp", "RRSP"], ["fhsa", "FHSA"], ["tfsa", "TFSA"], ["resp", "RESP"]],
  US: [["401k", "401(k)"], ["ira", "Traditional IRA"], ["roth", "Roth IRA"], ["hsa", "HSA"]],
  UK: [["pension", "Workplace pension"], ["sipp", "SIPP"], ["isa", "Stocks & Shares ISA"], ["lisa", "Lifetime ISA"]],
  IN: [["epf", "EPF"], ["ppf", "PPF"], ["nps", "NPS"]],
  SG: [["cpf", "CPF (OA / SA / MA)"]],
  JP: [["ideco", "iDeCo"], ["nisa", "NISA"]],
};
export function retirementTypes(country) {
  return (RET[country] || [["retirement", "Retirement / pension account"]]).map(([id, label]) => ({ id, label }));
}
export const retirementLabel = (country, id) => retirementTypes(country).find((x) => x.id === id)?.label || "Retirement account";

// ---- loans --------------------------------------------------------------------------------------------
export function loanPayment(principal, ratePct, years) {
  const n = Math.round(years * 12), r = ratePct / 100 / 12;
  if (!(principal > 0) || !(n > 0)) return 0;
  return r > 0 ? (principal * r) / (1 - Math.pow(1 + r, -n)) : principal / n;
}
export function loanBalanceAfter(principal, ratePct, years, paid) {
  const n = Math.round(years * 12), r = ratePct / 100 / 12, k = Math.max(0, Math.min(paid, n));
  if (k >= n) return 0;
  if (r === 0) return principal * (1 - k / n);
  const pmt = loanPayment(principal, ratePct, years);
  return Math.max(0, principal * Math.pow(1 + r, k) - pmt * ((Math.pow(1 + r, k) - 1) / r));
}
export function monthsElapsed(startISO, todayISO) {
  const a = new Date(startISO + "T00:00:00Z"), b = new Date(todayISO + "T00:00:00Z");
  let m = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
  if (b.getUTCDate() < a.getUTCDate()) m -= 1;
  return Math.max(0, m);
}
// A debt created from the loan calculator keeps its terms (`amort`) and the balance follows the schedule.
export function liabilityBalance(l, today = isoToday()) {
  if (l.amort) return loanBalanceAfter(l.amort.principal, l.amort.rate, l.amort.years, monthsElapsed(l.amort.start, today));
  return Number(l.balance) || 0;
}
export function liabilityPayment(l) { return l.amort ? loanPayment(l.amort.principal, l.amort.rate, l.amort.years) : Number(l.payment) || 0; }

// ---- totals -------------------------------------------------------------------------------------------
// Everything is converted to the profile currency; a missing FX rate falls back to the face value and is reported.
export function netWorthTotals(profile, { investedValue = 0, fxTable, today = isoToday() } = {}) {
  const cur = profile.currency || "USD";
  const missing = new Set();
  const conv = (amt, from) => { const c = convert(amt, from || cur, cur, fxTable); if (c === null) { missing.add(from); return amt; } return c; };
  const accounts = (profile.accounts || []).map((a) => ({ ...a, value: conv(Number(a.balance) || 0, a.currency) }));
  const debts = (profile.liabilities || []).map((l) => ({ ...l, value: conv(liabilityBalance(l, today), l.currency) }));
  const cashLike = accounts.reduce((s, a) => s + a.value, 0);
  const assets = cashLike + investedValue;
  const liabilities = debts.reduce((s, l) => s + l.value, 0);
  return { assets, liabilities, net: assets - liabilities, invested: investedValue, accounts, debts, missingFx: [...missing].filter(Boolean) };
}

// ---- snapshots ----------------------------------------------------------------------------------------
export const snapshotPoint = (tot, cur, today = isoToday()) => ({ d: today, a: round(tot.assets), l: round(tot.liabilities), n: round(tot.net), i: round(tot.invested), c: cur });
const round = (n) => Math.round(n * 100) / 100;

export function snapshotDue(snaps, cadence, today = isoToday()) {
  const last = snaps[snaps.length - 1];
  if (!last) return true;
  if (cadence === "weekly") return (ms(today) - ms(last.d)) / DAY >= 7;
  return last.d.slice(0, 7) !== today.slice(0, 7);
}
export function maybeSnapshot(snaps, point, cadence) {
  return snapshotDue(snaps, cadence, point.d) ? [...snaps, point] : snaps;
}
export function saveSnapshotNow(snaps, point) {
  const i = snaps.findIndex((s) => s.d === point.d);
  if (i >= 0) { const c = snaps.slice(); c[i] = point; return c; }
  return [...snaps, point].sort((a, b) => (a.d < b.d ? -1 : 1));
}

// Points for the chart. Snapshots in a different currency are skipped (they would mislead). The live value is the last point.
export function chartSeries(snaps, live, range, today = isoToday()) {
  const cutoff = range === "1M" ? ms(today) - 31 * DAY : range === "1Y" ? ms(today) - 366 * DAY : -Infinity;
  const pts = snaps.filter((s) => s.c === live.c && ms(s.d) >= cutoff && s.d !== live.d);
  return [...pts, live];
}
